(() => {
  if (customElements.get("lg-delivery")) return;

  class LGDelivery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";

      this.productInfo = this.closest("product-info");
      this.variants = this.readJSON("[data-lg-delivery-variants]", {});
      this.cartQuantities = this.readJSON(
        "[data-lg-delivery-cart-quantities]",
        {}
      );
      this.texts = this.readJSON("[data-lg-delivery-texts]", {
        normal: "",
        alternative: "",
      });
      this.variantId = String(this.dataset.currentVariantId || "");
      this.cartRefreshSequence = 0;

      this.onQuantityChange = this.onQuantityChange.bind(this);
      this.onVariantChanged = this.onVariantChanged.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onQuantityUpdate = this.onQuantityUpdate.bind(this);

      this.productInfo?.addEventListener("change", this.onQuantityChange);
      document.addEventListener("variant:changed", this.onVariantChanged);

      if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== "undefined") {
        this.unsubscribeCart = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate
        );
        this.unsubscribeQuantity = window.FoxThemeEvents.subscribe(
          PUB_SUB_EVENTS.quantityUpdate,
          this.onQuantityUpdate
        );
      }

      this.evaluate();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener("change", this.onQuantityChange);
      document.removeEventListener("variant:changed", this.onVariantChanged);
      this.unsubscribeCart?.();
      this.unsubscribeQuantity?.();
    }

    readJSON(selector, fallback) {
      try {
        const node = this.querySelector(selector);
        return node ? JSON.parse(node.textContent) : fallback;
      } catch (error) {
        console.warn("Lola Glamour delivery data could not be parsed.", error);
        return fallback;
      }
    }

    getQuantityInput() {
      if (!this.productInfo) return null;
      return this.productInfo.querySelector('input[name="quantity"]');
    }

    getRequestedQuantity() {
      const input = this.getQuantityInput();
      if (!input) return 1;
      const value = Number.parseInt(input.value, 10);
      if (Number.isFinite(value) && value > 0) return value;
      const min = Number.parseInt(input.dataset.min || input.min || "1", 10);
      return Number.isFinite(min) && min > 0 ? min : 1;
    }

    getCurrentVariantId() {
      const input = this.productInfo?.querySelector(
        'form[data-type="add-to-cart-form"] input[name="id"]'
      );
      return String(input?.value || this.variantId || "");
    }

    onQuantityChange(event) {
      if (!event.target.matches('input[name="quantity"]')) return;
      this.evaluate();
    }

    onVariantChanged(event) {
      const nextId = event.detail?.variant?.id;
      if (!nextId) return;

      const liveProductInfo = this.closest("product-info");
      if (!liveProductInfo || liveProductInfo.hasAttribute("data-replaced")) return;

      this.variantId = String(nextId);
      requestAnimationFrame(() => this.evaluate());
    }

    onQuantityUpdate() {
      requestAnimationFrame(() => this.evaluate());
    }

    async onCartUpdate() {
      const sequence = ++this.cartRefreshSequence;
      try {
        const root =
          window.FoxThemeSettings?.routes?.root ||
          window.Shopify?.routes?.root ||
          "/";
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        const state = await response.json();

        if (sequence !== this.cartRefreshSequence || !state?.items) return;

        const quantities = {};
        state.items.forEach((item) => {
          const id = String(item.variant_id || item.id || "");
          if (!id) return;
          quantities[id] = (quantities[id] || 0) + Number(item.quantity || 0);
        });
        this.cartQuantities = quantities;
        this.evaluate();
      } catch (error) {
        console.warn("Lola Glamour delivery cart state could not be refreshed.", error);
      }
    }

    evaluate() {
      const variantId = this.getCurrentVariantId();
      if (!variantId) return;
      this.variantId = variantId;

      const variant = this.variants[variantId];
      const normal = String(this.texts.normal || "").trim();
      const alternative = String(this.texts.alternative || "").trim();
      const cartQuantity = Number(this.cartQuantities[variantId] || 0);
      const requestedQuantity = this.getRequestedQuantity();

      const shouldUseAlternative = Boolean(
        variant &&
          variant.tracked === true &&
          variant.policy === "continue" &&
          cartQuantity + requestedQuantity > Number(variant.stock) &&
          alternative
      );

      const value = shouldUseAlternative ? alternative : normal;
      const line = this.querySelector("[data-lg-delivery-line]");
      const valueNode = this.querySelector("[data-lg-delivery-value]");
      if (!line || !valueNode) return;

      valueNode.textContent = value;
      line.hidden = !value;
    }
  }

  customElements.define("lg-delivery", LGDelivery);
})();
